import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useTheme } from '@/contexts/ThemeContext';
import { useProject } from '@/contexts/ProjectContext';
import {
  Zap, LayoutList, Settings2, ClipboardEdit, Scale, TrendingUp,
  ArrowUpRight, ArrowDownLeft, Grid3X3, List, Sparkles,
} from 'lucide-react';

import PCCMRegister from '@/components/commissioning/PCCMRegister';
import PCCMConfiguration from '@/components/commissioning/PCCMConfiguration';
import PCCMProgressEntry from '@/components/commissioning/PCCMProgressEntry';
import PCCMContributionMargins from '@/components/commissioning/PCCMContributionMargins';
import PCCMAnalytics from '@/components/commissioning/PCCMAnalytics';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import GuidedImportWizard from '@/components/shared/GuidedImportWizard';
import { PRECOMMISSIONING_CONFIG, COMMISSIONING_CONFIG } from '@/components/shared/importFieldConfigs';
import { FileSpreadsheet } from 'lucide-react';

const TABS = [
  { id: 'register', label: 'Subsystem Register', icon: LayoutList },
  { id: 'insights', label: 'AI Insights', icon: Sparkles },
  { id: 'config', label: 'Configuration', icon: Settings2 },
  { id: 'progress', label: 'Progress Entry', icon: ClipboardEdit },
  { id: 'margins', label: 'Contribution Margins', icon: Scale },
  { id: 'analytics', label: 'S-Curve & Analytics', icon: TrendingUp },
];

export default function CommissioningSchedule() {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('register');
  const { selectedProject, projectId: ctxProjectId } = useProject();
  const activeProjectId = selectedProject?.project_id;
  const [importMode, setImportMode] = useState(null); // 'precomm' | 'comm' | null

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="PRECOMM_COMMISSIONING" defaultOpen={true} compact={true} />}

      <div className="space-y-4 p-4 md:p-6" data-testid="commissioning-schedule-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: '#EF444420', border: '1px solid #EF444440' }}>
              <Zap className="w-5 h-5" style={{ color: '#EF4444' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Pre-Commissioning & Commissioning Schedule</h1>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                System-based commissioning tracking with stage gate progress and handover management
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px]" style={{ background: '#3b82f610', border: '1px solid #3b82f630', color: '#3b82f6' }}>
              <ArrowDownLeft className="w-3 h-3" /> Roll-down
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px]" style={{ background: '#10b98110', border: '1px solid #10b98130', color: '#10b981' }}>
              <ArrowUpRight className="w-3 h-3" /> Roll-up
            </div>
            <button onClick={() => setImportMode('precomm')} data-testid="pccm-import-precomm-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-[1.02]"
              style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
              <FileSpreadsheet className="w-3.5 h-3.5" /> Import Pre-Comm
            </button>
            <button onClick={() => setImportMode('comm')} data-testid="pccm-import-comm-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-[1.02]"
              style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
              <FileSpreadsheet className="w-3.5 h-3.5" /> Import Commissioning
            </button>
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              data-testid="pccm-project-label">Project: {selectedProject?.name || 'Select a project'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors"
                style={{
                  color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-muted)',
                  background: activeTab === tab.id ? 'var(--card-bg)' : 'transparent',
                  borderBottom: activeTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent'
                }}
                data-testid={`pccm-tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'register' && <PCCMRegister projectId={activeProjectId} />}
        {activeTab === 'insights' && <ModuleInsightsDashboard projectId={activeProjectId} moduleKey="commissioning" color="#EAB308" deepLinkBase="/project-controls/commissioning" />}
        {activeTab === 'config' && <PCCMConfiguration projectId={activeProjectId} />}
        {activeTab === 'progress' && <PCCMProgressEntry projectId={activeProjectId} />}
        {activeTab === 'margins' && <PCCMContributionMargins projectId={activeProjectId} />}
        {activeTab === 'analytics' && <PCCMAnalytics projectId={activeProjectId} />}
        <GuidedImportWizard
          open={importMode === 'precomm'}
          onOpenChange={(v) => !v && setImportMode(null)}
          projectId={activeProjectId}
          onImportComplete={() => setImportMode(null)}
          {...PRECOMMISSIONING_CONFIG(activeProjectId)}
        />
        <GuidedImportWizard
          open={importMode === 'comm'}
          onOpenChange={(v) => !v && setImportMode(null)}
          projectId={activeProjectId}
          onImportComplete={() => setImportMode(null)}
          {...COMMISSIONING_CONFIG(activeProjectId)}
        />
      </div>
    </DashboardLayout>
  );
}
